import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import clsx from 'clsx';
import { LoginModal } from '../LoginModal';
import { RegisterModal } from '../RegisterModal';
import { PublicLanguageSelect } from '../PublicLanguageSelect';
import { LEGAL_PATHS } from '../../legal/paths';

export interface PublicLayoutActions {
  openLogin: () => void;
  openRegister: () => void;
}

/** Abschnitte der Startseite, die Kopf- und Fusszeile als Sprungziele verlinken. Die Startseite
 * scrollt beim Wechsel des Hashes selbst dorthin (siehe Landing.tsx), damit die Links auch von
 * der Paketseite aus funktionieren. */
const SECTION_LINKS = [
  { hash: '#funktionen', labelKey: 'landing:nav.features' },
  { hash: '#ablauf', labelKey: 'landing:nav.workflow' },
  { hash: '#sicherheit', labelKey: 'landing:nav.security' },
] as const;

const Logo: React.FC<{ size?: 'md' | 'sm' }> = ({ size = 'md' }) => (
  <span className="inline-flex items-center gap-2.5">
    <img
      src="/canebase-icon.png"
      alt=""
      className={clsx('w-auto object-contain', size === 'md' ? 'h-9' : 'h-7')}
    />
    <span
      className={clsx(
        'font-bold tracking-tight text-dark-50',
        size === 'md' ? 'text-xl' : 'text-lg'
      )}
    >
      canebase
    </span>
  </span>
);

/** Rahmen der oeffentlichen Seiten (Startseite, Pakete): fixierte Kopfzeile, Fusszeile und die
 * Anmelde-/Registrierungsdialoge. Die Seite selbst bekommt die Dialog-Oeffner als Render-Prop. */
export const PublicLayout: React.FC<{
  children: (actions: PublicLayoutActions) => React.ReactNode;
}> = ({ children }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const openLogin = () => {
    setShowRegisterModal(false);
    setShowLoginModal(true);
    setMobileMenuOpen(false);
  };

  const openRegister = () => {
    setShowLoginModal(false);
    setShowRegisterModal(true);
    setMobileMenuOpen(false);
  };

  const handleAuthSuccess = () => {
    window.location.href = '/dashboard';
  };

  const closeMenu = () => setMobileMenuOpen(false);
  const pricingActive = pathname === '/pakete';

  return (
    // overflow-x-clip statt -hidden: "hidden" macht den Container zum Scroll-Container, und die
    // sticky Kopfzeile wuerde dann an ihm statt am Viewport haften (also gar nicht).
    <div className="flex min-h-screen flex-col overflow-x-clip bg-dark-900 text-dark-50">
      <header
        className={clsx(
          'sticky top-0 z-40 border-b transition-colors duration-300',
          scrolled || mobileMenuOpen
            ? 'border-dark-700/60 bg-dark-900/85 backdrop-blur-xl'
            : 'border-transparent bg-transparent'
        )}
      >
        <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-4 sm:px-6 lg:h-[4.5rem] lg:px-8">
          <Link to="/" className="shrink-0" aria-label="canebase" onClick={closeMenu}>
            <Logo />
          </Link>

          <div className="hidden items-center gap-1 lg:flex">
            {SECTION_LINKS.map((link) => (
              <Link
                key={link.hash}
                to={{ pathname: '/', hash: link.hash }}
                className="rounded-lg px-3 py-2 text-sm font-medium text-dark-300 transition-colors hover:text-dark-50"
              >
                {t(link.labelKey)}
              </Link>
            ))}
            <Link
              to="/pakete"
              aria-current={pricingActive ? 'page' : undefined}
              className={clsx(
                'rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:text-dark-50',
                pricingActive ? 'text-dark-50' : 'text-dark-300'
              )}
            >
              {t('landing:nav.pricing')}
            </Link>
          </div>

          <div className="flex items-center gap-2">
            <PublicLanguageSelect className="hidden lg:flex" />
            <button onClick={openLogin} className="btn hidden text-sm text-dark-200 hover:text-dark-50 lg:inline-flex">
              {t('landing:nav.login')}
            </button>
            <button onClick={openRegister} className="btn btn-primary btn-sm hidden sm:inline-flex">
              {t('landing:nav.register')}
            </button>
            <button
              onClick={() => setMobileMenuOpen((o) => !o)}
              className="icon-btn -mr-2 lg:hidden"
              aria-label={t('landing:nav.menuLabel')}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>

        {mobileMenuOpen && (
          <div className="border-t border-dark-700/60 lg:hidden">
            <div className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-4 sm:px-6">
              {SECTION_LINKS.map((link) => (
                <Link
                  key={link.hash}
                  to={{ pathname: '/', hash: link.hash }}
                  onClick={closeMenu}
                  className="rounded-lg px-3 py-2.5 text-base font-medium text-dark-200 hover:bg-dark-800"
                >
                  {t(link.labelKey)}
                </Link>
              ))}
              <Link
                to="/pakete"
                onClick={closeMenu}
                className="rounded-lg px-3 py-2.5 text-base font-medium text-dark-200 hover:bg-dark-800"
              >
                {t('landing:nav.pricing')}
              </Link>
              <div className="mt-3 grid grid-cols-2 gap-2 border-t border-dark-700/60 pt-4">
                <button onClick={openLogin} className="btn btn-outline w-full">
                  {t('landing:nav.login')}
                </button>
                <button onClick={openRegister} className="btn btn-primary w-full">
                  {t('landing:nav.register')}
                </button>
              </div>
              <PublicLanguageSelect className="mt-3 justify-center" />
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children({ openLogin, openRegister })}</main>

      <footer className="border-t border-dark-700/60 bg-dark-950/30">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:grid-cols-2 sm:px-6 lg:grid-cols-[1.6fr_1fr_1fr_1fr] lg:px-8">
          <div>
            <Link to="/" aria-label="canebase">
              <Logo size="sm" />
            </Link>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-dark-400">{t('landing:footer.tagline')}</p>
          </div>

          <div>
            <h2 className="text-sm font-semibold text-dark-100">{t('landing:footer.product')}</h2>
            <ul className="mt-4 space-y-3 text-sm">
              {SECTION_LINKS.map((link) => (
                <li key={link.hash}>
                  <Link to={{ pathname: '/', hash: link.hash }} className="text-dark-400 transition-colors hover:text-dark-50">
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/pakete" className="text-dark-400 transition-colors hover:text-dark-50">
                  {t('landing:nav.pricing')}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-sm font-semibold text-dark-100">{t('landing:footer.account')}</h2>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <button onClick={openLogin} className="text-dark-400 transition-colors hover:text-dark-50">
                  {t('landing:nav.login')}
                </button>
              </li>
              <li>
                <button onClick={openRegister} className="text-dark-400 transition-colors hover:text-dark-50">
                  {t('landing:nav.register')}
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-sm font-semibold text-dark-100">{t('landing:footer.language')}</h2>
            <PublicLanguageSelect className="mt-4" />
          </div>
        </div>

        <div className="border-t border-dark-700/60">
          <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-6 text-xs text-dark-400 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
            <span>{t('landing:footer.copyright', { year: new Date().getFullYear() })}</span>
            <nav aria-label={t('landing:footer.legal')} className="flex gap-5">
              <Link to={LEGAL_PATHS.terms} className="transition-colors hover:text-dark-50">
                {t('landing:footer.terms')}
              </Link>
              <Link to={LEGAL_PATHS.privacy} className="transition-colors hover:text-dark-50">
                {t('landing:footer.privacy')}
              </Link>
            </nav>
          </div>
        </div>
      </footer>

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSwitchToRegister={openRegister}
        onLoginSuccess={handleAuthSuccess}
      />
      <RegisterModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onSwitchToLogin={openLogin}
        onRegisterSuccess={handleAuthSuccess}
      />
    </div>
  );
};
